import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { loginUser, getCurrentUser, logoutUser, getStoredUser } from '../services/authService';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(() => getStoredUser());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Check if user is already logged in
    const checkLoggedIn = async () => {
      try {
        const user = await getCurrentUser();
        setCurrentUser(user);
      } catch (err) {
        setCurrentUser(null);
        console.log("Not logged in or token expired");
      } finally {
        setLoading(false);
      }
    };

    checkLoggedIn();
  }, []);

  const login = useCallback(async (email, password) => {
    setError(null);
    try {
      const user = await loginUser(email, password);
      setCurrentUser(user);
      return user;
    } catch (err) {
      setError(err.message || 'Failed to login');
      throw err;
    }
  }, []);

  const logout = useCallback(async () => {
    await logoutUser();
    setCurrentUser(null);
  }, []);

  const updateCurrentUser = useCallback((user) => {
    if (!user) return;
    setCurrentUser((prev) => {
      if (!prev) return user;
      // Check if all fields in incoming user match prev; if identical, keep reference to avoid rerenders
      const isIdentical = Object.keys(user).every((k) => prev[k] === user[k]);
      if (isIdentical) {
        return prev;
      }
      return { ...prev, ...user };
    });
    try {
      const existing = JSON.parse(localStorage.getItem('user') || '{}');
      localStorage.setItem('user', JSON.stringify({ ...existing, ...user }));
    } catch (e) {
      // ignore
    }
  }, []);

  return (
    <AuthContext.Provider value={{ currentUser, loading, error, login, logout, updateCurrentUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
